import { CheckSquare, GitBranch, MessageSquare, Paperclip } from 'lucide-react';
import { DONE_CATEGORIES, PRIORITY_LABELS, type TaskSummaryDto } from '@cw/shared';
import { AvatarStack } from '@/design-system/components/avatar';
import { Badge } from '@/design-system/components/badge';
import { cn } from '@/lib/utils';
import { TaskDue } from './due';
import { PRIORITY_TONE } from '@/features/projects/meta';

export const isDoneTask = (t: TaskSummaryDto) =>
  (DONE_CATEGORIES as readonly string[]).includes(t.status.category);

/** Indicadores compactos: subtarefas, checklist, comentários, anexos. */
export function TaskCounters({ t }: { t: TaskSummaryDto }) {
  const items = [
    t.subtaskCount > 0 && {
      icon: GitBranch,
      label: `${t.subtaskDone}/${t.subtaskCount}`,
      title: 'Subtarefas',
    },
    t.checklistCount > 0 && {
      icon: CheckSquare,
      label: `${t.checklistDone}/${t.checklistCount}`,
      title: 'Checklist',
    },
    t.commentCount > 0 && { icon: MessageSquare, label: String(t.commentCount), title: 'Comentários' },
    t.attachmentCount > 0 && { icon: Paperclip, label: String(t.attachmentCount), title: 'Anexos' },
  ].filter(Boolean) as { icon: typeof GitBranch; label: string; title: string }[];
  if (!items.length) return null;
  return (
    <span className="flex items-center gap-2.5 text-[11.5px] text-fg-subtle">
      {items.map(({ icon: Icon, label, title }) => (
        <span key={title} className="tabular inline-flex items-center gap-1" title={title}>
          <Icon className="size-3.5" />
          {label}
        </span>
      ))}
    </span>
  );
}

/** Card de tarefa no Kanban (ref. 01: prazo no topo, título, prioridade e pessoas embaixo). */
export function TaskCard({
  task: t,
  dragging,
  onOpen,
  showProject,
}: {
  task: TaskSummaryDto;
  dragging?: boolean;
  onOpen: () => void;
  showProject?: boolean;
}) {
  const done = isDoneTask(t);
  return (
    <div
      role="button"
      tabIndex={-1}
      onClick={onOpen}
      className={cn(
        'relative overflow-hidden rounded-[12px] bg-raised p-3 text-left shadow-[inset_0_0_0_1px_var(--color-line)] transition-[background,transform]',
        !dragging && 'cursor-pointer hover:bg-overlay',
        dragging && 'rotate-[2.5deg] shadow-lift ring-1 ring-line-strong',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <TaskDue iso={t.internalDueAt} done={done} />
        {(t.priority === 'urgent' || t.priority === 'high') && (
          <Badge tone={PRIORITY_TONE[t.priority]} className="h-5 px-2 text-[11px]">
            {PRIORITY_LABELS[t.priority]}
          </Badge>
        )}
      </div>
      <p
        className={cn('mt-1.5 text-[13.5px] leading-snug font-medium', done && 'text-fg-muted line-through')}
      >
        {t.title}
      </p>
      {showProject && t.project && (
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-fg-muted">
          <span
            className="size-2 rounded-[3px]"
            style={{ background: `var(--color-label-${t.project.color})` }}
          />
          {t.project.name}
        </p>
      )}
      {t.itemsLabel && <p className="mt-1 truncate text-[12px] text-fg-muted">{t.itemsLabel}</p>}
      <div className="mt-3 flex items-center justify-between gap-2">
        <TaskCounters t={t} />
        <AvatarStack people={t.responsibles.map((p) => ({ name: p.name, src: p.avatarUrl }))} max={3} />
      </div>
    </div>
  );
}
